import type { RefObject } from 'react';
import React, { memo, useRef } from 'react';

import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import { DragIndicator } from '@mui/icons-material';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Paper } from '@mui/material';

import { SymbolContent } from '@/features/Dashboard/form/AppForm/components/ItemSelection/SymbolContent';
import type { TabSymbol } from '@/features/Dashboard/form/AppForm/context/types';

interface SortableItemProps {
  item: TabSymbol;
  sourceListId: string;
  itemIndex: number;
  container: RefObject<HTMLDivElement | null>;
}

const SortableItemComponent: React.FC<SortableItemProps> = ({
  item,
  sourceListId,
  itemIndex,
  container
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLElement | null>(null);
  useSortable({
    id: item.id,
    index: itemIndex,
    group: sourceListId,
    element: ref,
    handle: handleRef,
    type: 'item',
    accept: 'item',
    modifiers: [RestrictToVerticalAxis, RestrictToElement.configure({ element: container.current })]
  });

  return (
    <Box
      ref={ref}
      sx={{ position: 'relative' }}
    >
      <Paper
        sx={{
          my: 1,
          border: '1px solid',
          borderColor: 'white',
          py: '10px',
          px: '12px',
          pl: '32px',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease',
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Box
            ref={handleRef}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab'
            }}
          >
            <DragIndicator sx={{ color: 'white', flexShrink: 0, fontSize: 16 }} />
          </Box>
          {sourceListId === 'active' ? (
            <VisibilityOutlinedIcon
              sx={{
                fontSize: 16,
                color: 'primary.main'
              }}
            />
          ) : (
            <VisibilityOffOutlinedIcon
              sx={{
                fontSize: 16,
                color: 'gx.textDisabled'
              }}
            />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <SymbolContent
              index={itemIndex}
              symbol={item}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export const SortableItem = memo(SortableItemComponent);
